// MuranoMatchInvite — feature-level prominence. Full-bleed tinted band,
// dominant CTA, direct benefit copy.

import { motion } from "framer-motion";
import { localeHref, useLocale } from "@/lib/i18n";

const MURANO_LAMP =
  "https://cdn.shopify.com/s/files/1/0562/8516/7668/products/image_2a8e1f78-32a1-48c6-9528-c0bd493b629a.heic?v=1668872354&width=1200";

export function MuranoMatchInvite() {
  const locale = useLocale();

  const eyebrow = locale === "da" ? "Murano-matchmaker" : "Murano matchmaker";
  const titleA = locale === "da" ? "Ved du ikke hvor du skal starte?" : "Don't know where to start?";
  const titleB = locale === "da" ? "Vi vælger for dig." : "We'll choose for you.";
  const body =
    locale === "da"
      ? "Fem spørgsmål. Tredive sekunder. Ingen mail."
      : "Five questions. Thirty seconds. No email.";
  const cta = locale === "da" ? "Start matchmakeren" : "Start the matchmaker";

  const quizHref = localeHref(locale, "/murano/match");

  return (
    <section
      data-tour="murano-match"
      className="relative w-full"
      style={{
        background:
          "linear-gradient(180deg, oklch(0.96 0.012 85) 0%, oklch(0.94 0.025 350 / 0.55) 100%)",
      }}
    >
      <div className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-44">
        <motion.a
          href={quizHref}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="group relative grid items-center gap-12 md:grid-cols-[0.85fr_1.15fr] md:gap-20"
        >
          <div className="relative mx-auto aspect-square w-full max-w-[460px] md:max-w-none">
            <div
              aria-hidden
              className="absolute inset-[6%] rounded-full transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              style={{
                background:
                  "radial-gradient(circle at 35% 30%, oklch(0.92 0.06 350 / 0.95), oklch(0.93 0.08 95 / 0.55) 45%, oklch(0.90 0.06 230 / 0.4) 75%, transparent 100%)",
                filter: "blur(2px)",
              }}
            />
            <div className="absolute inset-[4%] overflow-hidden rounded-full">
              <img
                src={MURANO_LAMP}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                style={{
                  objectPosition: "50% 38%",
                  transform: "scale(1.35)",
                  filter: "drop-shadow(0 30px 40px oklch(0.20 0.012 350 / 0.20))",
                }}
              />
            </div>
          </div>

          <div className="relative">
            <p className="eyebrow text-terracotta">{eyebrow}</p>

            <h2 className="mt-5 font-display text-[clamp(2.6rem,6vw,5rem)] leading-[0.98] tracking-tight text-foreground">
              {titleA}{" "}
              <em className="display-italic text-terracotta">{titleB}</em>
            </h2>
            <p className="mt-7 text-base leading-relaxed text-foreground/75 md:text-lg">
              {body}
            </p>

            <div className="mt-10">
              <span className="inline-flex items-center gap-3 rounded-full bg-foreground px-10 py-5 text-[12px] font-medium uppercase tracking-[0.26em] text-background transition-all group-hover:bg-terracotta group-hover:translate-x-1">
                {cta}
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </span>
            </div>
          </div>
        </motion.a>
      </div>
    </section>
  );
}
